import { Localized } from "@fluent/react"
import { Alert, Box, Button, ButtonGroup, Container, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material"
import { useState } from "react"
import { AppBlockGeneric, AppRowGeneric } from "../ursOffer/importedUrsSlice"
import { BlockMeta } from "../urs/ImportedSupplierUrsPreview"
import { Clear } from "@mui/icons-material"
import QuestionMarkIcon from '@mui/icons-material/QuestionMark'
import { AppId } from "../../app/appTypes"
import CheckIcon from '@mui/icons-material/Check'
import { ExternalRequirementComment, ExternalRequirementNote, ExternalUrsOffer, ExternalUrsOfferRequirement, postExternalUrsRequirementComment } from "./externalOfferSlice"
import { LoadingButton } from "@mui/lab"
import FeedbackIcon from '@mui/icons-material/Feedback';
import { useAppDispatch } from "../../app/hooks"

export interface ImportedOfferRequirementsPanelProps {
    urs: ExternalUrsOffer
    token: string
}
export const NoRequirements = () => {
    return <Typography gutterBottom component="div" color="textSecondary" variant="h5">
        <Localized id="urs-no-requirements">Brak wymagań.</Localized>
    </Typography>
}

export const ImportedExternalUrsContent = ({ urs, token }: ImportedOfferRequirementsPanelProps) => {
    const { id  } = urs

    const ursRequirements = urs.types[0].requirements
    let blocks: AppBlockGeneric<ExternalUrsOfferRequirement>[] = []
    let metas: BlockMeta[] = []
    for (let i = 0; i < ursRequirements.length; i++) {
        const r = ursRequirements[i]
        const { jsonContent } = r
        if (jsonContent) {
            const meta: BlockMeta = JSON.parse(jsonContent) as BlockMeta
            switch (meta.type) {
                case 'h':
                    blocks.push({
                        type: 'h',
                        content: meta.content,
                        data: r,
                    })
                    break
                case 'p':
                    blocks.push({
                        type: 'p',
                        content: meta.content,
                        data: r,
                    })
                    break
                case 'ol':
                    blocks.push({
                        type: 'ol',
                        items: r.childRequirements.map(childReq => {
                            return {
                                content: childReq.name,
                                data: childReq,
                            }
                        }),
                        data: r,
                    })
                    break
                case 'ul':
                    blocks.push({
                        type: 'ul',
                        items: r.childRequirements.map(childReq => {
                            return {
                                content: childReq.name,
                                data: childReq,
                            }
                        }),
                        data: r,
                    })
                    break
                case 'th':
                case 'tr':
                    if (i > 0) {
                        const prev = metas[i-1]
                        if ((prev.type === 'th' || prev.type === 'tr') && prev.tableId === meta.tableId) {
                            const tab = blocks[blocks.length-1]
                            if (tab.type === 'table') {
                                tab.rows.push({
                                    type: meta.type,
                                    cols: meta.cols,
                                    plain: r.name,
                                    data: r,
                                    split: meta.split,
                                })
                            } else {
                                console.log("[BUG] expected 'table'")
                            }
                        } else {
                            blocks.push({
                                type: 'table',
                                colsCount: meta.cols.length,
                                rows: [{
                                    type: meta.type,
                                    cols: meta.cols,
                                    plain: r.name,
                                    data: r,
                                    split: meta.split,
                                }]
                            })
                        }
                    } else {
                        blocks.push({
                            type: 'table',
                            colsCount: meta.cols.length,
                            rows: [{
                                type: meta.type,
                                cols: meta.cols,
                                plain: r.name,
                                data: r,
                                split: meta.split,
                            }]
                        })
                    }
                    break
            }
            metas.push(meta)
        }
    }

    return <Container component={Paper} sx={{
        height: 'calc(100vh - 300px)',
        overflow: 'auto',
        width: '100%',
        minWidth: '100%',
        m: 0,
    }} maxWidth='lg'>
        {blocks.map((b, i) => <RenderBlock block={b} offerId={id} token={token} comments={urs.comments} />)}
    </Container>
}

const RenderBlock = (props: { block : AppBlockGeneric<ExternalUrsOfferRequirement>, offerId: AppId, token: string, comments: ExternalRequirementComment[] }) => {
    const { block, offerId, token, comments } = props
    switch (block.type) {
        case 'h':
            return <Box sx={{ display: 'flex' }}>
                <Typography  variant="h6" sx={{ flexGrow: 1 }} >
                    <div dangerouslySetInnerHTML={{ __html: block.content }}></div>
                </Typography>
                <Box sx={{
                    ml: .5,
                    width: '400px',
                    borderLeft: '1px dashed var(--gray-300)',
                    flex: 'none',
                }}></Box>
            </Box>
        case 'p':
        return <Box sx={{
                display: 'flex',
            }}>
            <Typography sx={{
                flexGrow: 1,
            }} component='div' variant="body1" dangerouslySetInnerHTML={{ __html: block.content }}>
            </Typography>
            <Box sx={{
                ml: .5,
                width: '400px',
                borderLeft: '1px dashed var(--gray-300)',
                flex: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pl: 1,
            }}>
                <CommentSection offerId={offerId} r={block.data} token={token} comments={comments} />
            </Box>
        </Box>
        case 'ol':
            return <ol>
                {block.items.map(i => <li>
                    <Box sx={{
                        display: 'flex',
                    }}>
                        <Typography sx={{
                            flexGrow: 1,
                        }} component='div' variant="body1" dangerouslySetInnerHTML={{ __html: i.content }}>
                        </Typography>
                        <Box sx={{
                            ml: .5,
                            width: '400px',
                            borderLeft: '1px dashed var(--gray-300)',
                            flex: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pl: 1,
                        }}>
                            <CommentSection offerId={offerId} r={i.data} token={token} comments={comments} />
                        </Box>
                    </Box>
                </li>)}
            </ol>
        case 'ul':
            return <ul>
                {block.items.map(i => <li>
                    <Box sx={{
                        display: 'flex',
                        p: .5,
                        [`&:hover`]: {
                            backgroundColor: 'var(--slate-100)',
                        },
                    }}>
                        <Typography sx={{
                            flexGrow: 1,
                        }} component='div' variant="body1" dangerouslySetInnerHTML={{ __html: i.content }}>
                        </Typography>
                        <Box sx={{
                            ml: .5,
                            width: '400px',
                            borderLeft: '1px dashed var(--gray-300)',
                            flex: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pl: 1,
                        }}>
                            <CommentSection offerId={offerId} r={i.data} token={token} comments={comments} />
                        </Box>
                    </Box>
                </li>)}
            </ul>
        case 'table':
            return <AppTable offerId={offerId} colsCount={block.colsCount} rows={block.rows} token={token} comments={comments} />
    }
}

function AppTable(props: { offerId: AppId, colsCount: number, rows: AppRowGeneric<ExternalUrsOfferRequirement>[], token: string, comments: ExternalRequirementComment[] }) {
    const { colsCount, rows, offerId, token, comments } = props
    const firstTrIdx = rows.findIndex(r => r.type === 'tr')
    const ths = rows.slice(0, firstTrIdx)
    const trs = rows.slice(firstTrIdx)
    return <TableContainer sx={{
        my: 1,
    }} component={Paper}>
        <Table>
            {ths.length > 0 && <TableHead>
                {ths.map(row => <TableRow>
                    {row.cols.map(col => <TableCell sx={{ minWidth: '150px' }}><span dangerouslySetInnerHTML={{ __html: col}}></span></TableCell>)}
                    <TableCell className="comment-cell" sx={{ width: '400px', borderLeft: '1px dashed var(--gray-300)', }}></TableCell>
                </TableRow>)}
            </TableHead>}
            <TableBody>
                {trs.map(row => <TableRow sx={{
                    [`&:hover`]: {
                        backgroundColor: 'var(--slate-100)',
                    },
                }}>
                    {row.cols.map(col => <TableCell>
                        <p dangerouslySetInnerHTML={{ __html: col }}></p>
                    </TableCell>)}
                    <TableCell sx={{
                        width: '400px',
                        borderLeft: '1px dashed var(--gray-300)',
                        // display: 'flex',
                        // alignItems: 'center',
                        // justifyContent: 'center',
                        textAlign: 'center',
                        verticalAlign: 'top',
                    }}>
                        <CommentSection offerId={offerId} r={row.data} token={token} comments={comments} />
                    </TableCell>
                </TableRow>)}
            </TableBody>
        </Table>
    </TableContainer>;
}

const CommentSection = (props: { r: ExternalUrsOfferRequirement, offerId: AppId, token: string, comments: ExternalRequirementComment[] }) => {
    const { r, offerId, token, comments } = props
    const dispatch = useAppDispatch()

    const [reqNote, setReqNote] = useState<ExternalRequirementNote | null>(r.note)
    const [reqComment, setReqComment] = useState(r.comment)
    const [note, setNote] = useState<null | string>(() => {
        const i = comments.findIndex(x => x.ursRequirementId == r.id)
        if (i >= 0) {
            return comments[i].message
        } else {
            return null
        }
    })
    const [draftNote, setDraftNote] = useState('')
    const [editNote, setEditNote] = useState(false)
    const [saving, setSaving] = useState(false)

    const handlePostCommentClick = async () => {
        try {
            setSaving(true)
            await dispatch(postExternalUrsRequirementComment({
                token,
                comment: {
                    id: "",
                    ursOfferId: offerId,
                    ursRequirementId: r.id,
                    message: draftNote,
                    author: "",
                    userId: null,
                    date: new Date().toISOString(),
                    parentMessageId: null,
                    subMessages: [],
                    status: "",
                    guid: '',
                    type: 'text',
                },
            })).unwrap()
            setNote(draftNote)
            setEditNote(false)
        } catch (e) {
            /* handle error */
            console.log(e)
        } finally {
            setSaving(false)
        }
    }

    if (reqNote === null) {
        return <Box sx={{
            display: 'felx',
            flexDirection: 'row',
            gap: 1,
        }}>
        </Box>
    }  else {
        let label = <></>
        switch (reqNote) {
            case 'F':
                label = <Alert sx={{ width: '100%' }}
                    icon={<CheckIcon fontSize="inherit" />}
                    severity="success">
                    {reqComment ? reqComment : 'OK'}
                </Alert>
                break
            case 'U':
                label = <Alert sx={{ width: '100%' }}
                    icon={<Clear fontSize="inherit" />} severity="error">{reqComment ? reqComment : 'NOT OK'}</Alert>
                break
            case 'D':
                label = <Alert sx={{ width: '100%' }}
                    icon={<QuestionMarkIcon fontSize="inherit" />} variant='outlined' severity="success">{reqComment ? reqComment : 'OK'}</Alert>
                break;
        }
        return <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: .5,
            width: '100%',
        }}>
            {label}
            {reqNote === 'D' && note === null && !editNote && <Box sx={{
                display: 'flex',
                flexDirection: 'row-reverse',
            }}>
                <Button startIcon={<FeedbackIcon sx={{ color: 'var(--red-400)' }} />} size='small' variant='outlined' onClick={() => setEditNote(true)}>Add comment</Button>
            </Box>}
            {note && <Box sx={{
                backgroundColor: 'var(--yellow-300)',
                border: '1px solid var(--gray-400)',
                borderRadius: 1,
                p: 1,
            }}>
                <Box>
                    <Typography component='span' variant="body2" fontWeight='bold'>Client </Typography>
                    <Typography component='span' variant="body2" color='text.secondary' fontSize='small'>27-07-2025 11:33</Typography>
                </Box>
                <Typography variant="body2">{note ?? ''}</Typography>
            </Box>}
            {editNote && <Box sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                p: 1,
                border: '1px solid var(--slate-300)',
                borderRadius: '.5rem',
                backgroundColor: 'var(--slate-50)',
            }}>
                <TextField
                    sx={{ backgroundColor: '#fff' }}
                    fullWidth
                    multiline
                    minRows={2}
                    size='small'
                    inputProps={{ maxLength: 1000 }}
                    value={draftNote}
                    onChange={e => setDraftNote(e.target.value)}
                    label={<Localized id="urs-offer-req-comment"><span>Komentarz</span></Localized>}
                />
                <ButtonGroup>
                    <LoadingButton loading={saving} size='small' onClick={handlePostCommentClick}>
                        <Localized id='save'>Save</Localized>
                    </LoadingButton>
                    <LoadingButton loading={saving} size='small' color='error' onClick={() => setEditNote(false)}>
                        <Localized id='cancel'>Cancel</Localized>
                    </LoadingButton>
                </ButtonGroup>
            </Box>}
        </Box>
    }
}
